"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  quote: "Current Pulse Electric completely overhauled our home's wiring. Their team was precise, clean, and finished the job exactly on schedule. true professionals.",
  author: "Sarah Jenkins",
  position: "Homeowner",
  projectType: "Residential Upgrade",
  location: "Metro City",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700070/img/residential_wiring_project.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700070/img/residential_wiring_project.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700070/img/residential_wiring_project.png-wvc-sizes",
  caption: "Full residential panel upgrade, 2024"
}, {
  quote: "When our warehouse lost power, they arrived within the hour. Their emergency response saved our inventory and got us back online before the morning shift.",
  author: "Logistics Solutions Inc.",
  position: "Commercial Client",
  projectType: "Emergency Repair",
  location: "Industrial District",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700070/img/commercial_emergency_repair.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700070/img/commercial_emergency_repair.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700070/img/commercial_emergency_repair.png-wvc-sizes",
  caption: "Critical commercial power restoration"
}, {
  quote: "The attention to detail during our office build-out was incredible. Every circuit is labeled and every outlet is perfectly placed. Highly recommended.",
  author: "Mark Thompson",
  position: "Business Owner",
  projectType: "Commercial Installation",
  location: "Downtown Hub",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700070/img/office_installation_project.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700070/img/office_installation_project.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700070/img/office_installation_project.webp-wvc-sizes",
  caption: "Corporate office electrical fit-out"
}];
export default function ClientExperiences() {
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [isPaused, setIsPaused] = React.useState(false);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  React.useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => setCurrent(api.selectedScrollSnap()));
  }, [api]);
  React.useEffect(() => {
    if (!api || isPaused) return;
    const interval = setInterval(() => {
      if (api.canScrollNext()) {
        api.scrollNext();
      } else {
        api.scrollTo(0);
      }
    }, 7000);
    return () => clearInterval(interval);
  }, [api, isPaused]);
  return <section data-section-id="898" id="client-experiences" className="dark bg-background py-20 md:py-32" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
      <div className="container mx-auto px-6 lg:px-12">
        {}
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 md:mb-24 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-8">
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-6">
              05 // Client Success
            </p>
            <h2 className="font-default text-3xl md:text-5xl lg:text-[3rem] font-bold leading-[1.1] text-foreground tracking-tighter">
              Powering <span className="text-primary">Success</span> for our partners.
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end items-end gap-3">
            <Button variant="outline" size="icon" onClick={() => api?.scrollPrev()} className="size-14 border-2 border-primary bg-transparent hover:bg-primary hover:text-primary-foreground text-primary" aria-label="Previous testimonial">
              <ChevronLeft className="size-5" />
            </Button>
            <Button variant="outline" size="icon" onClick={() => api?.scrollNext()} className="size-14 border-2 border-primary bg-transparent hover:bg-primary hover:text-primary-foreground text-primary" aria-label="Next testimonial">
              <ChevronRight className="size-5" />
            </Button>
          </div>
        </div>

        {}
        <Carousel setApi={setApi} opts={{
        loop: true,
        align: "start"
      }} className="w-full">
          <CarouselContent>
            {testimonials.map((testimonial, i) => <CarouselItem data-index={i} key={i}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
                  {}
                  <div className="lg:col-span-5">
                    <div className="relative aspect-[4/5] overflow-hidden bg-card shadow-lg">
                      <img src={testimonial.image} data-wvc-srcset={testimonial.srcset} data-wvc-sizes={testimonial.sizes} alt={testimonial.author} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    </div>
                    <p className="font-mono text-xs text-muted-foreground mt-4 tracking-widest">
                      {testimonial.caption}
                    </p>
                  </div>

                  {}
                  <div className="lg:col-span-6 lg:col-start-7 flex flex-col justify-start pt-2">
                    <Quote className="size-16 md:size-20 text-primary mb-6" strokeWidth={1.5} fill="currentColor" />
                    <blockquote className="font-default text-xl md:text-2xl lg:text-[1.75rem] font-medium leading-[1.4] text-foreground tracking-tight">
                      {testimonial.quote}
                    </blockquote>

                    <div className="mt-10">
                      <div className="h-px w-full bg-border mb-6" />
                      <p className="font-default text-base font-bold text-foreground mb-2">
                        {testimonial.author}
                      </p>
                      <p className="font-mono text-xs uppercase tracking-widest text-primary">
                        {testimonial.projectType} — {testimonial.location}
                      </p>
                    </div>
                  </div>
                </div>
              </CarouselItem>)}
          </CarouselContent>
        </Carousel>

        {}
        <div className="flex items-center gap-3 mt-12 lg:mt-16 lg:ml-[calc(50%+1rem)]">
          {testimonials.map((_, i) => <Button data-index={i} key={i} onClick={() => api?.scrollTo(i)} variant="ghost" size="sm" className={`h-2 px-0 transition-all duration-300 rounded-none ${current === i ? "w-10 bg-primary hover:bg-primary" : "w-2 bg-muted hover:bg-muted-foreground"}`} aria-label={`Go to testimonial ${i + 1}`} />)}
        </div>
      </div>
    </section>;
}